import { useCallback } from 'react';

import { useFeedbackServiceSubmitFeedbackMutation } from '../api/generated-api';
import type { SubmitSidekickFeedback } from '../contexts/SidekickApiContext';

/**
 * Package-owned default for {@link SubmitSidekickFeedback}, backed by
 * the package's generated `FeedbackService` client.
 *
 * The feedback UI speaks in `rating` / `comment`; the wire speaks in
 * `thumbsUp` / `rationale`. This maps between them and drops blank
 * optional fields so an empty comment box never posts an empty string.
 *
 * @returns A callback that persists one feedback submission, rejecting on transport failure.
 */
export const useDefaultSubmitFeedback = (): SubmitSidekickFeedback => {
  const [submitFeedback] = useFeedbackServiceSubmitFeedbackMutation();

  return useCallback<SubmitSidekickFeedback>(
    async ({ rating, comment, category, ...input }) => {
      const rationale = comment?.trim();
      const normalizedCategory = category?.trim();

      await submitFeedback({
        submitFeedbackRequest: {
          ...input,
          thumbsUp: rating === 'up',
          ...(rationale !== undefined && rationale !== '' ? { rationale } : {}),
          ...(normalizedCategory !== undefined && normalizedCategory !== ''
            ? { category: normalizedCategory }
            : {}),
        },
      }).unwrap();
    },
    [submitFeedback]
  );
};
